import React from 'react';
import Card from '../../ui/Card';

const TYPE_META = {
  'participant-joined':       { label: 'joined',          color: 'text-emerald-600', icon: '↗' },
  'participant-reconnected':  { label: 'reconnected',     color: 'text-emerald-500', icon: '↺' },
  'participant-left':         { label: 'left',            color: 'text-red-600',     icon: '⊘' },
  'participant-removed':      { label: 'removed',         color: 'text-purple-600',  icon: '✕' },
  'participant-backgrounded': { label: 'backgrounded',    color: 'text-amber-600',   icon: '◐' },
  'participant-foregrounded': { label: 'returned',        color: 'text-emerald-500', icon: '↻' },
  'activity-launched':        { label: 'activity launched', color: 'text-blue-600',  icon: '▶' },
  'activity-closed':          { label: 'activity closed', color: 'text-neutral-700', icon: '■' },
  'response-received':        { label: 'responded',       color: 'text-primary-600', icon: '✓' },
  'subsession-started':       { label: 'subsession started', color: 'text-indigo-600', icon: '⊐' },
  'subsession-ended':         { label: 'subsession ended', color: 'text-indigo-500',  icon: '⊏' },
  'session-started':          { label: 'session started', color: 'text-emerald-700', icon: '▶' },
  'session-paused':           { label: 'session paused',  color: 'text-amber-600',   icon: '⏸' },
  'session-resumed':          { label: 'session resumed', color: 'text-emerald-600', icon: '▶' },
  'session-ended':            { label: 'session ended',   color: 'text-neutral-700', icon: '■' },
  'workbook-launched':        { label: 'workbook opened', color: 'text-blue-500',    icon: '📖' },
  'workbook-stopped':         { label: 'workbook closed', color: 'text-neutral-600', icon: '■' },
};

const formatTime = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
};

const renderLine = (e, participantNameById) => {
  const meta = TYPE_META[e.type] || { label: e.type, color: 'text-neutral-600', icon: '•' };
  // Backend doesn't always populate participantName on lifecycle events
  // (especially backgrounded/foregrounded sent on reconnected sockets that
  // haven't re-bound their name). Fall back to looking it up from the
  // participants snapshot before showing the generic "Someone".
  const lookedUp = e.participantId && participantNameById?.[String(e.participantId)];
  const who = e.participantName || lookedUp || 'Someone';
  const what = e.activityTitle || e.subSessionName || '';

  switch (e.type) {
    case 'participant-joined':       return `${who} joined`;
    case 'participant-reconnected':  return `${who} reconnected`;
    case 'participant-left':
      return `${who} left${e.metadata?.leaveReason ? ` — ${e.metadata.leaveReason.replace(/-/g, ' ')}` : ''}`;
    case 'participant-removed':      return `${who} was removed by trainer`;
    case 'participant-backgrounded': return `${who} backgrounded the app`;
    case 'participant-foregrounded': return `${who} returned to the app`;
    case 'activity-launched':        return `Activity launched: ${what}`;
    case 'activity-closed':          return `Activity closed${what ? `: ${what}` : ''}${e.metadata?.responseCount != null ? ` (${e.metadata.responseCount} responses)` : ''}`;
    case 'response-received':        return `${who} responded${what ? ` to ${what}` : ''}`;
    case 'subsession-started':       return `Subsession started${what ? `: ${what}` : ''}`;
    case 'subsession-ended':         return `Subsession ended${what ? `: ${what}` : ''}`;
    case 'session-started':          return 'Session started';
    case 'session-paused':           return 'Session paused';
    case 'session-resumed':          return 'Session resumed';
    case 'session-ended':            return `Session ended${e.metadata?.reason ? ` — ${e.metadata.reason}` : ''}`;
    case 'workbook-launched':        return `Workbook opened${e.metadata?.name ? `: ${e.metadata.name}` : ''}`;
    case 'workbook-stopped':         return 'Workbook closed';
    default:                         return `${meta.label}${who ? ` — ${who}` : ''}`;
  }
};

/**
 * Scrolling chronological event log. Newest at top. Capped at 200 events
 * client-side. `embedded` strips the outer Card chrome when used inside the
 * tabbed view on the monitor page.
 */
const LiveActivityFeed = ({ events = [], embedded = false, participants = [] }) => {
  const items = events.slice(0, 200);

  // Build a participantId → name map once so renderLine can fall back to it
  // for events that arrive without a participantName (e.g. backgrounded /
  // foregrounded after socket reconnect).
  const participantNameById = React.useMemo(() => {
    const map = {};
    participants.forEach((p) => {
      const id = p?._id || p?.id || p?.participantId;
      const name = p?.name || p?.participantName || (p?.firstName ? `${p.firstName} ${p.lastName || ''}`.trim() : null);
      if (id && name) map[String(id)] = name;
    });
    return map;
  }, [participants]);

  const body = items.length === 0 ? (
    <div className="py-12 text-center">
      <p className="text-sm text-neutral-500">No events yet</p>
      <p className="text-xs text-neutral-400 mt-1">Live updates will appear here</p>
    </div>
  ) : (
    <ol className="divide-y divide-neutral-100 max-h-[480px] overflow-y-auto">
      {items.map((e, idx) => {
        const meta = TYPE_META[e.type] || { label: e.type, color: 'text-neutral-600', icon: '•' };
        return (
          <li key={`${e._id || idx}-${e.ts}`} className="flex items-start gap-3 py-2.5 px-1">
            <span className="text-xs text-neutral-400 tabular-nums w-16 flex-shrink-0">{formatTime(e.ts)}</span>
            <span className={`flex-shrink-0 w-5 text-center ${meta.color}`}>{meta.icon}</span>
            <span className="text-sm text-neutral-800 flex-1">{renderLine(e, participantNameById)}</span>
          </li>
        );
      })}
    </ol>
  );

  if (embedded) return body;
  return <Card title="Live Event Feed" padding="md">{body}</Card>;
};

export default LiveActivityFeed;
